import { Filter, MessageSquarePlus, Search, Ticket as TicketIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { CustomerGate } from "../../components/AuthGate";
import { EmptyState, PageError, PageLoader } from "../../components/StateViews";
import { TicketCard } from "../../components/TicketCard";
import { apiEnvelope, asTicketList, queryString } from "../../lib/api";
import type { Ticket } from "../../lib/types";
import { usePagedCollection, COLLECTION_PAGE_SIZE } from "../../hooks/usePagedCollection";
import { CollectionPagination } from "../../components/CollectionPagination";

// Customer-facing groups; send every status to the API before pagination.
export const FILTERS = [
  { label: "All", value: "all" },
  { label: "Open", value: "new,open" },
  { label: "Waiting", value: "pending,on_hold" },
  { label: "Solved", value: "solved" },
  { label: "Closed", value: "closed" },
] as const;

function CustomerTicketList() {
  const [status, setStatus] = useState<(typeof FILTERS)[number]["value"]>("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const result = usePagedCollection<Ticket>(JSON.stringify([status, query]), page, setPage, async (page, signal) => {
    const response = await apiEnvelope<unknown>(`/tickets${queryString({ status: status === "all" ? undefined : status, query, page, limit: COLLECTION_PAGE_SIZE })}`, { signal });
    const data = asTicketList(response.data, response.meta);
    return { items: data.tickets, total: data.meta.total ?? data.tickets.length };
  });

  return (
    <section ref={result.topRef} className="page-stack">
      <header className="page-header">
        <div><p className="eyebrow">Your support history</p><h1>My tickets</h1><p>Follow updates and keep every reply in one conversation.</p></div>
        <Link className="button button--primary" to="/new"><MessageSquarePlus aria-hidden="true" size={17} /> New ticket</Link>
      </header>
      <div className="toolbar card">
        <label className="search-field">
          <Search aria-hidden="true" size={18} />
          <span className="sr-only">Search tickets</span>
          <input value={query} onChange={(event) => { setPage(1); setQuery(event.target.value); }} placeholder="Search by subject or ticket number" />
        </label>
        <div className="filter-pills" role="group" aria-label="Filter by status">
          <Filter aria-hidden="true" size={16} />
          {FILTERS.map((filter) => (
            <button key={filter.value} type="button" className={status === filter.value ? "is-active" : ""} aria-pressed={status === filter.value} onClick={() => { setPage(1); setStatus(filter.value); }}>{filter.label}</button>
          ))}
        </div>
      </div>
      {result.loading ? <PageLoader label="Loading your tickets…" /> : result.error ? (
        <PageError message={result.error} onRetry={() => void result.refresh()} />
      ) : result.items.length ? (
        <div className="ticket-list">{result.items.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} />)}</div>
      ) : (
        <EmptyState
          title={query || status !== "all" ? "No matching tickets" : "No support tickets yet"}
          description={query || status !== "all" ? "Try a broader search or another status." : "When you need us, create a ticket and the full conversation will appear here."}
          icon={<TicketIcon aria-hidden="true" size={26} />}
          action={<Link className="button button--primary" to="/new">Create your first ticket</Link>}
        />
      )}
      <CollectionPagination {...result} busy={result.refreshing} count={result.items.length} label="Ticket pages" />
    </section>
  );
}

export function TicketsPage() {
  return <CustomerGate><CustomerTicketList /></CustomerGate>;
}
